import { Fragment, useCallback, useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  api,
  type AlbLogResponse,
  type AppLogResponse,
  type EcsEventsResponse,
  type LogSourceInfo,
} from '../api/client'
import { useIsAdmin } from '../auth/AuthContext'
import { AdminOnly } from '../components/AdminOnly'
import { fmtDateTime, fmtTime } from '../utils/datetime'
import { AuditLogPane } from './AuditLog'

/**
 * Build a one-line summary of a structured record's most useful fields.
 *
 * The `request` lines were the reason this exists. They arrive fully populated:
 *
 *   {"msg":"request","http_method":"GET","http_path":"/api/runs",
 *    "http_status":200,"duration_ms":20.1,"role":"admin", ...}
 *
 * ...but the Message column rendered only `msg`, so every one of them displayed
 * as the bare word "request" and the pane looked full of contentless noise. The
 * data was never missing; it was being thrown away at render. Deleting the lines
 * — the other option on the table — would have discarded the application's only
 * per-request latency telemetry.
 *
 * Returns null when there is nothing worth inlining, so unrecognised records fall
 * back to `msg` alone rather than showing a misleading partial summary.
 */
function summarizeFields(fields: Record<string, unknown>): string | null {
  const f = fields as Record<string, string | number | undefined>

  // HTTP request lines.
  if (f.http_method && f.http_path) {
    const parts = [`${f.http_method} ${f.http_path}`]
    if (f.http_status !== undefined) parts.push(`→ ${f.http_status}`)
    if (f.duration_ms !== undefined) parts.push(`(${f.duration_ms}ms)`)
    return parts.join(' ')
  }

  // Storage operations — the object, not just the bucket.
  if (f.storage_op && f.storage_uri) {
    const parts = [`${f.storage_op} ${f.storage_uri}`]
    if (f.size_bytes !== undefined) parts.push(`${f.size_bytes}B`)
    if (f.duration_ms !== undefined) parts.push(`(${f.duration_ms}ms)`)
    return parts.join(' ')
  }

  // SMTP sends — surface the remote message ID, the whole point of capturing it.
  if (f.smtp_message_id || f.smtp_response) {
    return `id=${f.smtp_message_id ?? '(unparsed)'} reply=${f.smtp_response ?? '—'}`
  }

  return null
}

/** Colour for a structured record's http_status so a 5xx is visible without
 *  reading the number. Named distinctly from the ALB pane's local statusColor(),
 *  which takes a bare status string — the local one shadowed this at the call site
 *  and the typecheck caught it. */
function fieldStatusColor(fields: Record<string, unknown>): string | undefined {
  const s = Number((fields as Record<string, unknown>).http_status)
  if (!s) return undefined
  if (s >= 500) return '#ff6b6b'
  if (s >= 400) return '#ffa726'
  return '#81c784'
}

const RANGES: { label: string; hours: number }[] = [
  { label: '15 min', hours: 0.25 },
  { label: '1 hour', hours: 1 },
  { label: '4 hours', hours: 4 },
  { label: '12 hours', hours: 12 },
  { label: '24 hours', hours: 24 },
  { label: '3 days', hours: 72 },
  { label: '7 days', hours: 168 },
]

const LEVELS = ['', 'DEBUG', 'INFO', 'WARNING', 'ERROR', 'CRITICAL']

const TAIL_INTERVAL_MS = 10000

// The ALB pane is tailed far less often than the app pane, and that is deliberate
// rather than arbitrary. Fetching it is not a log-group query: it does an S3
// list_objects_v2 plus a get_object per matching gzip, re-scanning the WHOLE selected
// range every tick. At a 7-day range that is a meaningful S3 bill every ten seconds,
// for data that has a documented delivery lag of minutes — so a 10s poll cannot see
// anything a 60s poll misses.
const ALB_TAIL_INTERVAL_MS = 60000

// Pane order as shown: Application, Load Balancer, Audit, Deployments.
const PANE_ORDER = ['app', 'alb', 'audit', 'ecs'] as const
type Pane = (typeof PANE_ORDER)[number]

type PaneSpec = {
  label: string
  /** null means this pane is not tailable. */
  tailIntervalMs: number | null
  hasTimeRange: boolean
  /** Shown in the Tail control's tooltip. Required exactly when the pane is not
   *  tailable, so the tooltip can never claim the wrong reason. */
  notTailableBecause?: string
}

// One table instead of three scattered definitions. Typed as Record<Pane, …> rather
// than inferred from the literal: that is what makes an OMITTED pane a compile
// error. With an inferred type a missing pane just shrinks the union, and the new
// pane silently inherits the app pane's 10-second poll.
const PANES: Record<Pane, PaneSpec> = {
  app: { label: 'Application', tailIntervalMs: TAIL_INTERVAL_MS, hasTimeRange: true },
  alb: { label: 'Load balancer', tailIntervalMs: ALB_TAIL_INTERVAL_MS, hasTimeRange: true },
  audit: {
    label: 'Audit',
    tailIntervalMs: null,
    hasTimeRange: false,
    notTailableBecause: 'Audit entries are paginated and filtered, not streamed.',
  },
  // Tailed at the app pane's interval, because a rollout is the one thing on this page
  // that is CHANGING while you watch it. The pane was marked untailable on the grounds
  // that a fixed short history has nothing to tail, which reads the history and misses
  // the rollout: `deployments[].rolloutState` / `rolloutStateReason` and the newest
  // events are exactly what an operator refreshes for during a deploy, and they moved
  // while the pane sat still. Polling costs one `ecs.describe_services` call — bounded,
  // one API call per tick with no per-range scaling, unlike the ALB pane's S3 re-scan
  // that earned its own slower interval.
  ecs: { label: 'Deployments', tailIntervalMs: TAIL_INTERVAL_MS, hasTimeRange: false },
}

function isPane(value: string | null): value is Pane {
  return value !== null && (PANE_ORDER as readonly string[]).includes(value)
}

/** A pane is tailable exactly when the table gives it an interval. */
function paneTailIntervalMs(pane: Pane): number {
  return PANES[pane].tailIntervalMs ?? TAIL_INTERVAL_MS
}

// The tailable set, as the table implies it: exactly the panes with an interval.
//
// Written out rather than computed with `.filter()` on purpose. This array is read
// by tests/test_ui_nav_and_log_tail.py, which parses the literal to prove each pane
// is polled at the interval it declares and that a pane with no interval is never
// polled — a `.filter()` expression has no literal to parse and that guard would
// silently start passing on an empty list. The two cannot drift:
// tests/test_log_viewer_panes.py parses BOTH this array and the PANES table and
// fails if they disagree.
//
// Audit is the only pane left out, and for a reason no interval would fix: it is
// paginated and filtered rather than streamed, so re-fetching it does not show you
// anything newer.
const TAILABLE_PANES: readonly Pane[] = ['app', 'alb', 'ecs']

const TAIL_STORAGE_KEY = 'e2e.logviewer.tail'

/** Tail is ON by default, and the user's choice survives navigation.
 *
 * Default-on without persistence would be worse than leaving it off: `useState`
 * re-initialises on every mount, so someone who deliberately switched tailing off
 * would get it back merely by visiting Jobs and coming back, with no way to make it
 * stick. localStorage is the right scope — it is a per-operator display preference,
 * not something to round-trip to the server.
 */
function readTailPreference(): boolean {
  try {
    const stored = window.localStorage.getItem(TAIL_STORAGE_KEY)
    return stored === null ? true : stored === 'true'
  } catch {
    return true // private browsing / storage disabled — fall back to the default
  }
}

function writeTailPreference(value: boolean): void {
  try {
    window.localStorage.setItem(TAIL_STORAGE_KEY, String(value))
  } catch {
    // Not being able to remember the choice is not worth breaking the page over.
  }
}

/**
 * In-app log viewer.
 *
 * Four panes because there are four genuinely different views of "what happened",
 * and a failure usually shows up in only one of them: the app's own JSON records,
 * the ALB's view of the same traffic (which sees requests the app never received),
 * the audit log (who changed what), and ECS deployment events (which explain
 * rollouts, not requests). Correlating a config change against the application log
 * it caused used to mean navigating between two settings pages.
 */
export function LogViewer() {
  const isAdmin = useIsAdmin()

  // The pane lives in the URL, so a pane is linkable and `/settings/audit` can
  // redirect to one. `replace` keeps pane switching out of the back-button history.
  const [searchParams, setSearchParams] = useSearchParams()
  const paneParam = searchParams.get('pane')
  const pane: Pane = isPane(paneParam) ? paneParam : 'app'
  const setPane = (next: Pane) => {
    const params = new URLSearchParams(searchParams)
    params.set('pane', next)
    setSearchParams(params, { replace: true })
  }

  const [sources, setSources] = useState<LogSourceInfo | null>(null)
  const [hours, setHours] = useState(1)
  const [search, setSearch] = useState('')
  const [level, setLevel] = useState('')
  const [tail, setTail] = useState<boolean>(readTailPreference)
  const [loading, setLoading] = useState(false)
  const [lastFetch, setLastFetch] = useState<Date | null>(null)

  const [appData, setAppData] = useState<AppLogResponse | null>(null)
  const [albData, setAlbData] = useState<AlbLogResponse | null>(null)
  const [ecsData, setEcsData] = useState<EcsEventsResponse | null>(null)
  const [expanded, setExpanded] = useState<number | null>(null)

  // Held in a ref so the tail interval always calls the CURRENT fetch closure.
  // Without this the interval would capture the filters as they were when tail was
  // switched on and silently keep querying the old range.
  const fetchRef = useRef<() => void>(() => {})

  const load = useCallback(() => {
    // The Audit pane fetches nothing here — its child component owns its own
    // pagination and filters, so there is no query for this component to issue.
    if (pane === 'audit') return

    setLoading(true)
    const done = () => { setLoading(false); setLastFetch(new Date()) }
    if (pane === 'app') {
      api.getAppLogs({ hours, search: search || undefined, level: level || undefined })
        .then(setAppData).catch(e => setAppData({ events: [], truncated: false, error: e.message }))
        .finally(done)
    } else if (pane === 'alb') {
      api.getAlbLogs({ hours, search: search || undefined })
        .then(setAlbData).catch(e => setAlbData({ events: [], truncated: false, error: e.message }))
        .finally(done)
    } else if (pane === 'ecs') {
      // EXPLICIT, not a trailing else. This used to be `else`, which meant any pane
      // added later silently fetched ECS deployment events and rendered nothing.
      api.getEcsEvents()
        .then(setEcsData).catch(e => setEcsData({ events: [], deployments: [], error: e.message }))
        .finally(done)
    } else {
      setLoading(false)
    }
  }, [pane, hours, search, level])

  fetchRef.current = load

  useEffect(() => { load() }, [pane, hours, level])

  // Row expansion is per-pane and the index means nothing across panes.
  useEffect(() => { setExpanded(null) }, [pane])

  useEffect(() => {
    api.getLogSources().then(setSources).catch(() => {})
  }, [])

  // The effective interval comes from the pane table — see the constants above for
  // why the ALB pane is polled a sixth as often.
  const tailIntervalMs = paneTailIntervalMs(pane)
  const paneIsTailable = TAILABLE_PANES.includes(pane)

  useEffect(() => {
    writeTailPreference(tail)
  }, [tail])

  useEffect(() => {
    if (!tail || !paneIsTailable) return
    const id = setInterval(() => fetchRef.current(), tailIntervalMs)
    return () => clearInterval(id)
  }, [tail, paneIsTailable, tailIntervalMs])

  if (!isAdmin) {
    return (
      <div>
        <h2>Logs</h2>
        <AdminOnly what="The log viewer, which exposes full request and OCR content," />
      </div>
    )
  }

  // Every timestamp on this page goes through the shared helper, so it renders in
  // the zone the header clock advertises and carries that zone's abbreviation. The
  // previous formatter built an explicit `.toISOString() + 'Z'`, which is UTC no
  // matter where the operator is — and two other columns sliced the offset off an
  // ISO string, producing UTC with no marking at all.
  const ts = (ms: number | null | undefined) => fmtDateTime(ms)

  const levelColor = (l: string | null | undefined) => {
    switch ((l || '').toUpperCase()) {
      case 'CRITICAL':
      case 'ERROR': return '#ef5350'
      case 'WARNING': return '#ffa726'
      case 'DEBUG': return '#78909c'
      default: return '#66bb6a'
    }
  }

  const statusColor = (s: string | null | undefined) => {
    const n = parseInt(s || '', 10)
    if (Number.isNaN(n)) return '#9aa0a6'
    if (n >= 500) return '#ef5350'
    if (n >= 400) return '#ffa726'
    return '#66bb6a'
  }

  const notConfigured =
    (pane === 'app' && sources && !sources.app_log_group) ||
    (pane === 'alb' && sources && !sources.alb_log_bucket) ||
    (pane === 'ecs' && sources && !sources.ecs_cluster)

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', flexWrap: 'wrap', gap: '1rem' }}>
        <h2 style={{ marginBottom: '0.25rem' }}>Logs</h2>
        {sources && (
          <span style={{ fontSize: '0.75rem', color: '#9aa0a6' }}>
            {sources.region}
            {lastFetch ? ` · fetched ${fmtTime(lastFetch)}` : ''}
          </span>
        )}
      </div>

      {/* Pane selector, driven by the table so a new pane cannot be forgotten here. */}
      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '0.9rem', flexWrap: 'wrap' }}>
        {PANE_ORDER.map(key => (
          <button
            key={key}
            onClick={() => setPane(key)}
            style={{
              padding: '0.4rem 0.9rem', fontSize: '0.82rem', cursor: 'pointer',
              borderRadius: 4, border: '1px solid #333',
              background: pane === key ? '#FF4133' : '#1e1e1e',
              color: pane === key ? '#fff' : '#f5f5f5',
              fontWeight: pane === key ? 600 : 400,
            }}
          >
            {PANES[key].label}
          </button>
        ))}
      </div>

      {/* Controls — only the panes that query a time range get a range picker. The
          Audit pane has its own filters, and the Deployments pane returns a fixed
          short event history rather than a queryable stream. */}
      <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', alignItems: 'center', marginBottom: '0.8rem' }}>
        {PANES[pane].hasTimeRange && (
          <>
            <select value={hours} onChange={e => setHours(Number(e.target.value))} style={selectStyle}>
              {RANGES.map(r => <option key={r.hours} value={r.hours}>Last {r.label}</option>)}
            </select>
            {pane === 'app' && (
              <select value={level} onChange={e => setLevel(e.target.value)} style={selectStyle}>
                {LEVELS.map(l => <option key={l} value={l}>{l || 'All levels'}</option>)}
              </select>
            )}
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') load() }}
              placeholder={pane === 'app' ? 'Search (e.g. a run token)…' : 'Search (e.g. a path or IP)…'}
              style={{ ...selectStyle, minWidth: 260 }}
            />
            <button onClick={load} style={btnStyle}>Search</button>
          </>
        )}
        {pane !== 'audit' && (
          <button onClick={load} style={btnStyle} disabled={loading}>
            {loading ? 'Loading…' : 'Refresh'}
          </button>
        )}
        <label
          style={{
            fontSize: '0.8rem',
            display: 'flex',
            alignItems: 'center',
            gap: '0.35rem',
            cursor: paneIsTailable ? 'pointer' : 'not-allowed',
            opacity: paneIsTailable ? 1 : 0.5,
          }}
          title={
            paneIsTailable
              ? `Re-fetch every ${tailIntervalMs / 1000}s. Your choice is remembered.`
              : PANES[pane].notTailableBecause
          }
        >
          <input
            type="checkbox"
            checked={tail && paneIsTailable}
            disabled={!paneIsTailable}
            onChange={e => setTail(e.target.checked)}
          />
          {paneIsTailable ? `Tail (${tailIntervalMs / 1000}s)` : 'Tail (n/a)'}
        </label>
      </div>

      {notConfigured && (
        <div style={noteStyle}>
          <strong>This source is not configured.</strong>
          <p style={{ margin: '0.4rem 0 0', fontSize: '0.82rem', color: '#9aa0a6' }}>
            Run <code>infra/40_ecs.sh --apply</code> (and <code>infra/30_alb.sh --apply</code> for
            load balancer logs), then redeploy so the task picks up the new configuration.
          </p>
        </div>
      )}

      {/* --- APPLICATION PANE --- */}
      {pane === 'app' && appData && (
        <>
          {appData.error && <div style={errStyle}>{appData.error}</div>}
          {appData.page_limit_reached && (
            <div style={errStyle}>
              <strong>This window is too busy to read to the end.</strong> The query stopped
              paginating before it reached the newest events, so rows more recent than the
              first row below are missing. Narrow the time range or add a search term.
            </div>
          )}
          {appData.truncated && !appData.page_limit_reached && (
            <div style={noteStyle}>
              Showing the {appData.events.length} most recent matches. More matched — all of
              them <strong>older</strong> than the last row below. Narrow the range or add a
              search term to see further back.
            </div>
          )}
          {appData.events.length === 0 && !appData.error ? (
            <p style={{ color: '#9aa0a6' }}>No log events in this window.</p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr style={{ background: '#1e1e1e', textAlign: 'left' }}>
                    <th style={thStyle}>Time</th>
                    <th style={thStyle}>Level</th>
                    <th style={thStyle}>Logger</th>
                    <th style={thStyle}>Message</th>
                    <th style={thStyle}>Correlation</th>
                    <th style={thStyle} />
                  </tr>
                </thead>
                <tbody>
                  {appData.events.map((e, i) => {
                    const summary = summarizeFields(e.fields)
                    const hasDetail = Object.keys(e.fields).length > 0 || !e.structured
                    return (
                      // Keyed Fragment, not a bare <>: each event renders TWO
                      // sibling <tr>s and React needs the key on the outer element
                      // to reconcile them as one unit.
                      <Fragment key={i}>
                        <tr style={{ borderTop: '1px solid #2a2a2a', verticalAlign: 'top' }}>
                          <td style={{ ...tdStyle, whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: '0.72rem' }}>{ts(e.timestamp)}</td>
                          <td style={{ ...tdStyle, color: levelColor(e.level), fontWeight: 600, whiteSpace: 'nowrap' }}>
                            {e.level || (e.structured ? '—' : 'raw')}
                          </td>
                          <td style={{ ...tdStyle, fontSize: '0.72rem', color: '#9aa0a6' }}>{e.logger || '—'}</td>
                          <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.74rem', wordBreak: 'break-word', maxWidth: 520 }}>
                            <div>{e.msg}</div>
                            {summary && (
                              <div style={{ color: fieldStatusColor(e.fields) ?? '#9aa0a6', fontSize: '0.72rem', marginTop: '0.15rem' }}>
                                {summary}
                              </div>
                            )}
                          </td>
                          <td style={{ ...tdStyle, fontSize: '0.7rem', fontFamily: 'monospace' }}>
                            {e.run_token && <div style={{ color: '#82b1ff' }}>{e.run_token}</div>}
                            {e.request_id && <div style={{ color: '#9aa0a6' }}>{e.request_id}</div>}
                            {e.actor && <div style={{ color: '#777' }}>{e.actor}</div>}
                            {!e.run_token && !e.request_id && !e.actor && '—'}
                          </td>
                          <td style={tdStyle}>
                            {hasDetail && (
                              <button
                                onClick={() => setExpanded(expanded === i ? null : i)}
                                style={{ ...btnStyle, padding: '0.15rem 0.4rem', fontSize: '0.7rem' }}
                              >
                                {expanded === i ? 'Hide' : 'More'}
                              </button>
                            )}
                          </td>
                        </tr>
                        {expanded === i && (
                          // Inline, directly beneath the row that was clicked.
                          // This block previously rendered AFTER the entire table,
                          // so on a long pane the output appeared hundreds of pixels
                          // below the viewport and the button looked broken — the
                          // detail was rendering correctly the whole time, just
                          // nowhere the user would ever see it.
                          <tr style={{ background: '#141414' }}>
                            <td colSpan={6} style={{ padding: 0 }}>
                              <pre style={preStyle}>
                                {JSON.stringify(
                                  Object.keys(e.fields).length > 0 ? e.fields : e.raw,
                                  null, 2,
                                )}
                              </pre>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    )
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* --- LOAD BALANCER PANE --- */}
      {pane === 'alb' && albData && (
        <>
          {albData.error && <div style={errStyle}>{albData.error}</div>}
          {albData.delivery_lag_note && !albData.error && (
            <div style={noteStyle}>{albData.delivery_lag_note}</div>
          )}
          {albData.events.length === 0 && !albData.error ? (
            <p style={{ color: '#9aa0a6' }}>
              No access log entries in this window. Note the delivery lag above — very recent
              traffic will not appear yet.
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={tableStyle}>
                <thead>
                  <tr style={{ background: '#1e1e1e', textAlign: 'left' }}>
                    <th style={thStyle}>Time</th>
                    <th style={thStyle}>Client</th>
                    <th style={thStyle}>Request</th>
                    <th style={thStyle}>ELB</th>
                    <th style={thStyle}>Target</th>
                    <th style={thStyle}>Target time</th>
                  </tr>
                </thead>
                <tbody>
                  {albData.events.map((e, i) => (
                    <tr key={i} style={{ borderTop: '1px solid #2a2a2a' }}>
                      <td style={{ ...tdStyle, whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: '0.7rem' }}>
                        {fmtDateTime(e.time)}
                      </td>
                      <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.72rem' }}>{e.client_ip || '—'}</td>
                      <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.72rem', wordBreak: 'break-all', maxWidth: 460 }}>
                        <span style={{ color: '#9aa0a6' }}>{e.method}</span> {e.url}
                      </td>
                      <td style={{ ...tdStyle, color: statusColor(e.elb_status_code), fontWeight: 600 }}>{e.elb_status_code || '—'}</td>
                      <td style={{ ...tdStyle, color: statusColor(e.target_status_code) }}>{e.target_status_code || '—'}</td>
                      <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.72rem' }}>{e.target_processing_time || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* --- AUDIT PANE ---
          A child component, so its pagination, filters and sort effects stay where
          they were rather than becoming six more pieces of this component's state. */}
      {pane === 'audit' && <AuditLogPane />}

      {/* --- DEPLOYMENTS PANE --- */}
      {pane === 'ecs' && ecsData && (
        <>
          {ecsData.error && <div style={errStyle}>{ecsData.error}</div>}
          {ecsData.deployments.length > 0 && (
            <div style={{ marginBottom: '1rem' }}>
              <h3 style={{ fontSize: '0.9rem', margin: '0 0 0.5rem' }}>Current deployments</h3>
              <table style={tableStyle}>
                <thead>
                  <tr style={{ background: '#1e1e1e', textAlign: 'left' }}>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Rollout</th>
                    <th style={thStyle}>Tasks</th>
                    <th style={thStyle}>Task definition</th>
                    <th style={thStyle}>Started</th>
                  </tr>
                </thead>
                <tbody>
                  {ecsData.deployments.map((d, i) => (
                    <tr key={i} style={{ borderTop: '1px solid #2a2a2a' }}>
                      <td style={tdStyle}>{d.status}</td>
                      <td style={{ ...tdStyle, color: d.rollout_state === 'COMPLETED' ? '#66bb6a' : d.rollout_state === 'FAILED' ? '#ef5350' : '#ffa726', fontWeight: 600 }}>
                        {d.rollout_state}
                        {d.rollout_reason && (
                          <div style={{ fontSize: '0.7rem', color: '#9aa0a6', fontWeight: 400 }}>{d.rollout_reason}</div>
                        )}
                      </td>
                      <td style={tdStyle}>{d.running}/{d.desired}{d.pending ? ` (+${d.pending} pending)` : ''}</td>
                      <td style={{ ...tdStyle, fontFamily: 'monospace', fontSize: '0.72rem' }}>{d.task_definition}</td>
                      <td style={{ ...tdStyle, fontSize: '0.72rem', whiteSpace: 'nowrap' }}>
                        {fmtDateTime(d.created_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <h3 style={{ fontSize: '0.9rem', margin: '0 0 0.5rem' }}>Service events</h3>
          {ecsData.events.length === 0 && !ecsData.error ? (
            <p style={{ color: '#9aa0a6' }}>No recent service events.</p>
          ) : (
            <table style={tableStyle}>
              <tbody>
                {ecsData.events.map((e, i) => (
                  <tr key={i} style={{ borderTop: '1px solid #2a2a2a' }}>
                    <td style={{ ...tdStyle, whiteSpace: 'nowrap', fontFamily: 'monospace', fontSize: '0.72rem', width: 190 }}>{ts(e.timestamp)}</td>
                    <td style={{ ...tdStyle, fontSize: '0.78rem' }}>{e.message}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </>
      )}
    </div>
  )
}

const tableStyle: React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }
const thStyle: React.CSSProperties = { padding: '0.45rem 0.7rem', whiteSpace: 'nowrap' }
const tdStyle: React.CSSProperties = { padding: '0.4rem 0.7rem' }

const selectStyle: React.CSSProperties = {
  padding: '0.4rem 0.6rem', background: '#1e1e1e', color: '#f5f5f5',
  border: '1px solid #333', borderRadius: 4, fontSize: '0.8rem',
}

const btnStyle: React.CSSProperties = {
  padding: '0.4rem 0.7rem', background: '#262626', color: '#f5f5f5',
  border: '1px solid #333', borderRadius: 4, fontSize: '0.8rem', cursor: 'pointer',
}

const noteStyle: React.CSSProperties = {
  background: '#1a2a3a', border: '1px solid #24384a', borderRadius: 6,
  padding: '0.7rem 0.9rem', marginBottom: '0.8rem', fontSize: '0.82rem',
}

const errStyle: React.CSSProperties = {
  background: '#3a1e1e', border: '1px solid #5a2a2a', borderRadius: 6,
  padding: '0.7rem 0.9rem', marginBottom: '0.8rem', fontSize: '0.82rem', color: '#ef9a9a',
}

const preStyle: React.CSSProperties = {
  margin: '0.6rem 0 0', background: '#262626', border: '1px solid #333',
  padding: '0.7rem', borderRadius: 4, fontSize: '0.72rem', maxHeight: 320, overflow: 'auto',
}
